import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, theme, Drawer } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  DatabaseOutlined,
  UploadOutlined,
  FileTextOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 检测是否为移动设备
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenu: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  const adminMenuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      key: '/admin/employees',
      icon: <UserOutlined />,
      label: '员工管理',
      onClick: () => navigate('/admin/employees'),
    },
    {
      key: '/admin/datasets',
      icon: <DatabaseOutlined />,
      label: '数据集管理',
      onClick: () => navigate('/admin/datasets'),
    },
    {
      key: '/admin/upload',
      icon: <UploadOutlined />,
      label: '数据录入',
      onClick: () => navigate('/admin/upload'),
    },
    {
      key: '/admin/my-uploads',
      icon: <FileTextOutlined />,
      label: '我的上传',
      onClick: () => navigate('/admin/my-uploads'),
    },
  ];

  const employeeMenuItems = [
    {
      key: '/employee/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
      onClick: () => navigate('/employee/dashboard'),
    },
    {
      key: '/employee/upload',
      icon: <UploadOutlined />,
      label: '数据录入',
      onClick: () => navigate('/employee/upload'),
    },
    {
      key: '/employee/my-uploads',
      icon: <FileTextOutlined />,
      label: '我的上传',
      onClick: () => navigate('/employee/my-uploads'),
    },
  ];

  const menuItems = user?.is_admin ? adminMenuItems : employeeMenuItems;

  // 菜单项点击处理（移动端需要关闭抽屉）
  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  // 为移动端更新菜单项的点击处理
  const mobileAdminMenuItems = adminMenuItems.map(item => ({
    ...item,
    onClick: () => handleMenuClick(item.key),
  }));

  const mobileEmployeeMenuItems = employeeMenuItems.map(item => ({
    ...item,
    onClick: () => handleMenuClick(item.key),
  }));

  const mobileMenuItems = user?.is_admin ? mobileAdminMenuItems : mobileEmployeeMenuItems;

  // 侧边栏内容
  const sidebarContent = (
    <>
      <div className="demo-logo-vertical" style={{
        height: 32,
        margin: 16,
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 'bold',
        fontSize: isMobile ? '14px' : '16px',
      }}>
        {collapsed && !isMobile ? 'DM' : '数据管理系统'}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={isMobile ? mobileMenuItems : menuItems}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 桌面端固定侧边栏 */}
      {!isMobile && (
        <Sider 
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          width={200}
          collapsedWidth={80}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          styles={{ 
            body: { 
              padding: 0, 
              background: '#001529' 
            } 
          }}
          width={200}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout style={{ 
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 200), 
        transition: 'margin-left 0.2s',
        minHeight: '100vh',
      }}>
        <Header style={{ 
          padding: '0 16px', 
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => {
              if (isMobile) {
                setMobileDrawerOpen(!mobileDrawerOpen);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            style={{
              fontSize: '16px',
              width: isMobile ? 48 : 64,
              height: isMobile ? 48 : 64,
            }}
          />
          
          <Dropdown menu={{ items: userMenu }} placement="bottomRight">
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '0 8px',
            }}>
              <Avatar 
                icon={<UserOutlined />} 
                style={{ marginRight: isMobile ? 4 : 8 }}
                size={isMobile ? 'small' : 'default'}
              />
              {!isMobile && (
                <>
                  <span>{user?.username}</span>
                  <span style={{ 
                    marginLeft: 8, 
                    fontSize: '12px', 
                    color: '#666',
                  }}>
                    ({user?.is_admin ? '管理员' : '员工'})
                  </span>
                </>
              )}
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
